import React, { useContext, useEffect, useRef, useState } from "react"
import classNames from "classnames"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faChevronUp,
  faChevronDown,
  faProjectDiagram,
} from "@fortawesome/free-solid-svg-icons"
import hotkeys from "hotkeys-js"

import { useNana } from "../../hooks/useNana"
import NewProjectButton from "./projects_page/new_project_button"
import { ID, Project, ProjectStatus } from "../../../model/types"
import ProjectContentCollapsed from "./projects_page/project_content_collapsed"
import ProjectContentExpanded from "./projects_page/project_content_expanded"
import * as Commands from "../../../model/commands/index"
import FuzzyProjectFinder from "../../components/fuzzy_project_finder"
import { DeviceContext, KEYS } from "../../providers/device_context"
import ProjectHeader from "./projects_page/project_header"
import List from "./projects_page/projects_list"
import ProjectFilter, {
  ProjectFilterType,
  ProjectListsType,
  SortOrder,
  filterProjects,
} from "./projects_page/project_filter"

const StatusTabs = ({
  statusSelected,
  setStatusSelected,
  inProgressCount,
  completedCount,
  filter,
  setSortOrder,
}: {
  statusSelected: ProjectStatus
  setStatusSelected: (s: ProjectStatus) => void
  inProgressCount: number
  completedCount: number
  filter: ProjectFilterType
  setSortOrder: (s: SortOrder) => void
}) => {
  return (
    <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-600">
      <div className="flex items-center justify-start text-xs md:text-sm">
        <button
          onClick={() => setStatusSelected("in_progress")}
          className={`${
            statusSelected === "in_progress"
              ? "bg-gray-100 dark:bg-gray-800"
              : "bg-white dark:bg-gray-900"
          } py-2 px-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600`}
          data-testid="projects-in-progress-tab"
        >
          In progress ({inProgressCount})
        </button>
        <button
          onClick={() => setStatusSelected("completed")}
          className={`${
            statusSelected === "completed"
              ? "bg-gray-100 dark:bg-gray-800"
              : "bg-white dark:bg-gray-900"
          } py-2 px-4 font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500`}
          data-testid="projects-completed-tab"
        >
          Completed ({completedCount})
        </button>
      </div>

      <div className="text-xs cursor-pointer hover:text-blue-800 pr-4">
        {filter.sortOrder[statusSelected] == "newest-first" ? (
          <div
            className="flex items-center"
            onClick={() => setSortOrder("oldest-first")}
          >
            {statusSelected == "in_progress"
              ? "Updated recently"
              : "Completed recently first"}
            <FontAwesomeIcon icon={faChevronDown} className="ml-1 h-2" />
          </div>
        ) : (
          <div
            className="flex items-center"
            onClick={() => setSortOrder("newest-first")}
          >
            {statusSelected == "in_progress"
              ? "Oldest updated first"
              : "Oldest completed first"}
            <FontAwesomeIcon icon={faChevronUp} className="ml-1 h-2" />
          </div>
        )}
      </div>
    </div>
  )
}

const EmptyState = ({ anyIdeas }: { anyIdeas: boolean }) => {
  return (
    <div className="grow flex flex-col gap-8 items-center justify-start pt-4 md:pt-24 text-sm md:text-base text-gray-700 dark:text-gray-300">
      {anyIdeas ? (
        <FontAwesomeIcon
          icon={faProjectDiagram}
          className="text-2xl md:text-5xl text-slate-200 dark:text-slate-800"
        />
      ) : (
        <>
          <div>
            Use projects to organize your writing ideas and see drafts of
            project content.
          </div>
          <div>Click "New project" to the left to create one.</div>
        </>
      )}
    </div>
  )
}

export const ProjectsPageState: {
  lastSelectedTab: ProjectStatus | null
  openProjectId: ID | null
  isExpanded: boolean
  filter: ProjectFilterType
} = {
  lastSelectedTab: null,
  openProjectId: null,
  isExpanded: false,
  filter: {
    query: "",
    goal: null,
    sortOrder: { in_progress: "newest-first", completed: "newest-first" },
  },
}

export default function ProjectsPage() {
  const { model, commander, hub, initialLoadComplete } = useNana()
  const { platform } = useContext(DeviceContext)

  const [filter, setFilter1] = useState<ProjectFilterType>(
    ProjectsPageState.filter
  )
  const setFilter = (filter: ProjectFilterType) => {
    setFilter1(filter)
    ProjectsPageState.filter = filter
  }

  const [projects, setProjects] = useState<ProjectListsType>(
    model
      ? filterProjects(model, filter)
      : { in_progress: [] as Project[], completed: [] as Project[] }
  )

  const [openProjectId, setOpenProjectId1] = useState<ID | null>(
    ProjectsPageState.openProjectId
  )
  const setOpenProjectId = (id: ID | null) => {
    ProjectsPageState.openProjectId = id
    setOpenProjectId1(id)
    setIsExpanded(false)
  }

  const openProject = openProjectId ? model.getProject(openProjectId) : null
  const [isExpanded, setIsExpanded1] = useState(ProjectsPageState.isExpanded)
  const setIsExpanded = (expanded: boolean) => {
    ProjectsPageState.isExpanded = expanded
    setIsExpanded1(expanded)
  }
  const [statusSelected, setStatusSelected] = useState<ProjectStatus>(
    ProjectsPageState.lastSelectedTab || "in_progress"
  )
  const [fuzzyFinderOpen, setFuzzyFinderOpen] = useState(false)

  const setFilterAndUpdate = (newFilter: ProjectFilterType) => {
    setFilter(newFilter)
    setProjects(filterProjects(model, newFilter))
  }

  useEffect(() => {
    setProjects(filterProjects(model, filter))
  }, [initialLoadComplete])

  // A handy function that means we don't have to remove and add listeners
  // every time the search state changes
  const updateFunctionRef = useRef<(() => void) | null>(null)
  updateFunctionRef.current = () => {
    setProjects(filterProjects(model, filter))
  }

  useEffect(() => {
    const keys = [
      hub.on(
        "command:create-project",
        () => updateFunctionRef.current && updateFunctionRef.current()
      ),
      hub.on(
        "command:set-project-fields",
        () => updateFunctionRef.current && updateFunctionRef.current()
      ),
      hub.on(
        "command:delete-project",
        () => updateFunctionRef.current && updateFunctionRef.current()
      ),
    ]

    hotkeys(KEYS.openFinder[platform], "projects", function (event, _handler) {
      event.preventDefault()
      setFuzzyFinderOpen(true)
    })

    hotkeys(KEYS.undo[platform], "projects", function (event, handler) {
      event.preventDefault()
      commander.undo()
    })

    hotkeys(KEYS.redo[platform], "projects", function (event, handler) {
      event.preventDefault()
      commander.redo()
    })

    return () => {
      keys.forEach((k) => hub.remove(k))
      hotkeys.unbind(KEYS.openFinder[platform], "projects")
      hotkeys.unbind(KEYS.undo[platform], "projects")
      hotkeys.unbind(KEYS.redo[platform], "projects")
    }
  }, [])

  hotkeys.setScope("projects")

  return (
    <div className="flex min-h-0 w-full flex-auto justify-stretch pt-16 dark:bg-gray-950">
      {fuzzyFinderOpen && (
        <FuzzyProjectFinder
          includeCompleted={true}
          chooseProject={(project) => setOpenProjectId(project.id)}
          close={() => setFuzzyFinderOpen(false)}
        />
      )}
      <div className="flex w-full dark:bg-gray-950">
        <div
          className={classNames(
            "min-h-0 flex-col w-full md:w-2/5  border-r border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-900",
            {
              "hidden md:flex": openProjectId,
              flex: !openProjectId,
            }
          )}
        >
          <div className="flex gap-2 p-4">
            <div className="shrink-0">
              <NewProjectButton />
            </div>
            <div className="grow min-w-0">
              <ProjectFilter filter={filter} setFilter={setFilterAndUpdate} />
            </div>
          </div>
          <div className="grow mt-4 flex flex-col" data-test-id="project-lists">
            <StatusTabs
              {...{
                statusSelected,
                setStatusSelected: (tab) => {
                  ProjectsPageState.lastSelectedTab = tab
                  setStatusSelected(tab)
                },
                inProgressCount: projects.in_progress.length,
                completedCount: projects.completed.length,
                filter: filter,
                setSortOrder: (newSortOrder) => {
                  const newFilter = {
                    ...filter,
                    sortOrder: {
                      ...filter.sortOrder,
                      [statusSelected]: newSortOrder,
                    },
                  }
                  setFilter(newFilter)
                  setProjects(filterProjects(model, newFilter))
                },
              }}
            />
            <div
              className={classNames(
                "grow flex flex-col bg-white dark:bg-gray-900",
                {
                  hidden: statusSelected != "in_progress",
                }
              )}
            >
              <List
                projects={projects.in_progress}
                projectStatus={"in_progress"}
                openProject={openProject}
                setOpenProjectId={setOpenProjectId}
              />
            </div>
            <div
              className={classNames(
                "grow flex flex-col bg-white dark:bg-gray-900",
                {
                  hidden: statusSelected != "completed",
                }
              )}
            >
              <List
                projects={projects.completed}
                projectStatus={"completed"}
                openProject={openProject}
                setOpenProjectId={setOpenProjectId}
              />
            </div>
          </div>
        </div>
        <div
          className={classNames(
            "min-h-0 flex flex-col items-left w-full md:w-3/5",
            {
              "hidden md:flex": !openProjectId,
            }
          )}
        >
          {!openProjectId && (
            <EmptyState anyIdeas={projects.in_progress.length > 0} />
          )}
          {openProjectId && openProject && (
            <>
              <ProjectHeader
                projectId={openProjectId}
                setOpenProjectId={setOpenProjectId}
              />
              {!isExpanded && (
                <>
                  <ProjectContentCollapsed
                    project={openProject}
                    setMode={(mode) => setIsExpanded(mode === "draft")}
                  />
                </>
              )}
              {isExpanded && (
                <ProjectContentExpanded
                  project={openProject}
                  setMode={(mode) => setIsExpanded(mode === "draft")}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
